import Cookies from "js-cookie";
import { clearLocalPreferences } from "./localPreferences";
import { clearApplicationCache } from "./clearApplicationCache";

export const PREV_PATH_EXCLUDED_ROUTES = [
  "/login",
  "/two-factor-auth",
  "/two-factor-acknowledgement",
  "/link-account-login",
  "/forgot-password",
  "/login.asp",
  "/",
];

const PREV_PATH_KEY = "prevPath";
const PREV_PATH_MAX_AGE_MS = 2 * 60 * 60 * 1000;
const PREV_PATH_NO_USER_MAX_AGE_MS = 3 * 60 * 1000;

interface PrevPathState {
  path: string;
  timestamp: number;
  userId?: string | null;
}

const getPrevPathState = (): PrevPathState | null => {
  try {
    const raw = sessionStorage.getItem(PREV_PATH_KEY);
    if (!raw) {
      return null;
    }
    const data = JSON.parse(raw) as PrevPathState;
    if (typeof data?.path !== "string" || typeof data?.timestamp !== "number") {
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

const getCurrentUserId = (): string | null => {
  try {
    const raw =
      sessionStorage.getItem("token_decoded") || Cookies.get("token_decoded");
    if (!raw) {
      return null;
    }
    const decoded = JSON.parse(raw) as { oid?: string; sub?: string };
    return decoded?.oid ?? decoded?.sub ?? null;
  } catch {
    return null;
  }
};

const isExcludedRoute = (path: string): boolean => {
  const pathNormalized = path.split("?")[0];
  return PREV_PATH_EXCLUDED_ROUTES.some(
    (route) => pathNormalized === route || pathNormalized.startsWith(route + "/")
  );
};

const isPrevPathExpired = (state: PrevPathState): boolean => {
  const age = Date.now() - state.timestamp;
  const maxAge =
    state.userId != null && state.userId !== ""
      ? PREV_PATH_MAX_AGE_MS
      : PREV_PATH_NO_USER_MAX_AGE_MS;
  return age > maxAge;
};

const clearPrevPath = () => {
  sessionStorage.removeItem(PREV_PATH_KEY);
};

export const setPrevPathIfAllowed = (path: string): void => {
  if (isExcludedRoute(path)) {
    return;
  }
  const userId = getCurrentUserId();
  if (!userId) {
    return;
  }
  const state: PrevPathState = {
    path,
    timestamp: Date.now(),
    userId,
  };
  sessionStorage.setItem(PREV_PATH_KEY, JSON.stringify(state));
};

export const setPrevPathForRedirectAfterLogin = (path: string): void => {
  if (isExcludedRoute(path)) {
    return;
  }
  const state: PrevPathState = {
    path,
    timestamp: Date.now(),
    userId: null,
  };
  sessionStorage.setItem(PREV_PATH_KEY, JSON.stringify(state));
};

export const getStoredPrevPath = (): string | null => {
  const state = getPrevPathState();
  return state?.path ?? null;
};

const eraseCookie = (name: string) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
  Cookies.remove(name);
};

export const logout = async (fullLogout: boolean = true) => {
  const userId = getCurrentUserId();

  try {
    const token =
      sessionStorage.getItem("token") || Cookies.get("token") || null;
    const baseURL = process.env.REACT_APP_ROOT_API;
    if (baseURL) {
      await fetch(`${baseURL}/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ userId: userId ?? undefined }),
      });
    }
  } catch {
    // best-effort AccessLog; never block logout
  }

  if (userId) {
    clearLocalPreferences(userId);
  }

  const itemsToRemove = [
    "token",
    "refresh_token",
    "access_token",
    "access_timestamp",
    "token_decoded",
    "2FAuth",
    "ssoLogin",
    "username",
    "pendingLogin",
    "googleAuthAction",
    "linkedinAuthAction",
  ];

  itemsToRemove.forEach((item) => {
    sessionStorage.removeItem(item);
    eraseCookie(item);
  });

  const prevState = getPrevPathState();
  if (prevState?.userId != null && prevState.userId !== "") {
    clearPrevPath();
  }

  localStorage.setItem("logout", Date.now().toString());

  if (fullLogout) {
    await clearApplicationCache({ clearAllSessionStorage: true });
    window.location.href = "/";
  }
};

export const getRedirectPath = (): string | null => {
  const state = getPrevPathState();
  if (!state) {
    return null;
  }
  if (isPrevPathExpired(state)) {
    clearPrevPath();
    return null;
  }
  if (state.userId != null && state.userId !== "") {
    const currentUserId = getCurrentUserId();
    if (state.userId !== currentUserId || !currentUserId) {
      clearPrevPath();
      return null;
    }
  }
  return state.path;
};

export const getAndClearRedirectPath = (): string | null => {
  const state = getPrevPathState();
  if (!state) {
    return null;
  }
  if (isPrevPathExpired(state)) {
    clearPrevPath();
    return null;
  }
  if (state.userId != null && state.userId !== "") {
    const currentUserId = getCurrentUserId();
    if (state.userId !== currentUserId || !currentUserId) {
      clearPrevPath();
      return null;
    }
  }
  clearPrevPath();
  return state.path;
};
